import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ImageUploader from './components/ImageUploader';
import { saveAuthor, getAllAuthors, deleteAuthor, getAuthorStats } from '../utils/authorStorage';

const { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiUser, FiMail, FiGlobe, FiMapPin, FiTwitter, FiLinkedin, FiGithub, FiSearch, FiGrid, FiList, FiStar, FiCalendar, FiFileText, FiEye, FiTrendingUp } = FiIcons;

const AuthorManager = () => {
  const [authors, setAuthors] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [authorStats, setAuthorStats] = useState({});

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    slug: '',
    title: '',
    bio: '',
    avatar: '',
    email: '',
    website: '',
    location: '',
    yearsExperience: '',
    specialties: [],
    social: {
      twitter: '',
      linkedin: '',
      github: ''
    },
    achievements: [],
    languages: [],
    status: 'active',
    featured: false,
    joinDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadAuthors();
  }, []);

  // Listen for new author creation events
  useEffect(() => {
    const handleCreateNewAuthor = () => {
      setIsEditing(true);
      resetForm();
    };

    window.addEventListener('createNewAuthor', handleCreateNewAuthor);
    return () => window.removeEventListener('createNewAuthor', handleCreateNewAuthor);
  }, []);

  const loadAuthors = async () => {
    try {
      setIsLoading(true);
      const allAuthors = await getAllAuthors();
      setAuthors(Array.isArray(allAuthors) ? allAuthors : []);
      
      // Load stats for each author
      const stats = {};
      for (const author of allAuthors) {
        stats[author.id] = await getAuthorStats(author.name);
      }
      setAuthorStats(stats);
    } catch (error) {
      console.error('Error loading authors:', error);
      setAuthors([]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const authorData = {
        ...formData,
        id: editingAuthor?.id || generateSlug(formData.name),
        slug: formData.slug || generateSlug(formData.name),
        specialties: Array.isArray(formData.specialties) ? formData.specialties : formData.specialties.split(',').map(s => s.trim()).filter(Boolean),
        achievements: Array.isArray(formData.achievements) ? formData.achievements : formData.achievements.split('\n').map(a => a.trim()).filter(Boolean),
        languages: Array.isArray(formData.languages) ? formData.languages : formData.languages.split(',').map(l => l.trim()).filter(Boolean),
        yearsExperience: parseInt(formData.yearsExperience) || 0,
        createdAt: editingAuthor?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await saveAuthor(authorData);
      await loadAuthors();
      resetForm();
    } catch (error) {
      console.error('Error saving author:', error);
      alert('Failed to save author. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      slug: '',
      title: '',
      bio: '',
      avatar: '',
      email: '',
      website: '',
      location: '',
      yearsExperience: '',
      specialties: [],
      social: {
        twitter: '',
        linkedin: '',
        github: ''
      },
      achievements: [],
      languages: [],
      status: 'active',
      featured: false,
      joinDate: new Date().toISOString().split('T')[0]
    });
    setEditingAuthor(null);
    setIsEditing(false);
  };

  const handleEdit = (author) => {
    setEditingAuthor(author);
    setFormData({
      ...author,
      specialties: Array.isArray(author.specialties) ? author.specialties.join(', ') : author.specialties || '',
      achievements: Array.isArray(author.achievements) ? author.achievements.join('\n') : author.achievements || '',
      languages: Array.isArray(author.languages) ? author.languages.join(', ') : author.languages || '',
      social: author.social || { twitter: '', linkedin: '', github: '' }
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this author? This will affect all associated blog posts.')) {
      try {
        await deleteAuthor(id);
        await loadAuthors();
      } catch (error) {
        console.error('Error deleting author:', error);
        alert('Failed to delete author. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('social.')) {
      const socialField = name.split('.')[1];
      setFormData({
        ...formData,
        social: {
          ...formData.social,
          [socialField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }

    // Auto-generate slug from name
    if (name === 'name') {
      setFormData(prev => ({
        ...prev,
        name: value,
        slug: generateSlug(value)
      }));
    }
  };

  const handleImageUpload = (imageUrl) => {
    setFormData(prev => ({
      ...prev,
      avatar: imageUrl
    }));
  };

  const filteredAuthors = authors.filter(author => {
    const matchesSearch = 
      author.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      author.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      author.bio?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Light Editing Form View
  if (isEditing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {editingAuthor ? 'Edit Author' : 'New Author'}
          </h2>
          <motion.button
            onClick={resetForm}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 rounded-xl transition-all duration-300"
          >
            <SafeIcon icon={FiX} className="h-5 w-5" />
            <span>Cancel</span>
          </motion.button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-8 space-y-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="auto-generated-slug"
                  />
                </div>
              </div>

              {/* Professional Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Professional Title *</label>
                <input
                  type="text"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  placeholder="e.g., Senior AI Engineer & Prompt Engineering Specialist"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Biography *</label>
                <textarea
                  name="bio"
                  required
                  rows={6}
                  value={formData.bio}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="Write a detailed biography including background, expertise, and achievements..."
                />
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="author@aiximo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="https://authorwebsite.com"
                  />
                </div>
              </div>

              {/* Location and Experience */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="City, State/Country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience</label>
                  <input
                    type="number"
                    name="yearsExperience"
                    min="0"
                    max="50"
                    value={formData.yearsExperience}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Years"
                  />
                </div>
              </div>

              {/* Social Media Links */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">Social Media Profiles</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Twitter/X</label>
                    <input
                      type="url"
                      name="social.twitter"
                      value={formData.social.twitter}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      placeholder="https://twitter.com/username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">LinkedIn</label>
                    <input
                      type="url"
                      name="social.linkedin"
                      value={formData.social.linkedin}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      placeholder="https://linkedin.com/in/username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">GitHub</label>
                    <input
                      type="url"
                      name="social.github"
                      value={formData.social.github}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      placeholder="https://github.com/username"
                    />
                  </div>
                </div>
              </div>

              {/* Specialties */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specialties & Skills</label>
                <textarea
                  name="specialties"
                  rows={3}
                  value={formData.specialties}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="AI/ML Engineering, React.js, Node.js, Python, etc. (comma-separated)"
                />
                <p className="text-xs text-gray-500 mt-1">Separate each specialty with a comma</p>
              </div>

              {/* Achievements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Key Achievements</label>
                <textarea
                  name="achievements"
                  rows={4}
                  value={formData.achievements}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="PhD in Computer Science, MIT&#10;20+ published research papers&#10;Speaker at international conferences"
                />
                <p className="text-xs text-gray-500 mt-1">Each achievement on a new line</p>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
                <input
                  type="text"
                  name="languages"
                  value={formData.languages}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  placeholder="English, Spanish, Python, JavaScript, etc. (comma-separated)"
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Avatar Upload */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">Profile Photo</label>
                <ImageUploader
                  onUpload={handleImageUpload}
                  currentImage={formData.avatar}
                  label="Upload profile photo"
                />
              </div>

              {/* Status & Settings */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="featured" className="text-sm text-gray-700 flex items-center space-x-2">
                    <SafeIcon icon={FiStar} className="h-4 w-4 text-yellow-500" />
                    <span>Featured Author</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Join Date</label>
                  <input
                    type="date"
                    name="joinDate"
                    value={formData.joinDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
              </div>

              {/* Save Button */}
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiSave} className="h-5 w-5" />
                <span>{editingAuthor ? 'Update Author' : 'Save Author'}</span>
              </motion.button>
            </div>
          </div>
        </form>
      </motion.div>
    );
  }

  // Main Author Manager View
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Author Manager</h2>
          <p className="text-gray-600">Manage author profiles and information</p>
        </div>
        <motion.button
          onClick={() => setIsEditing(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="h-5 w-5" />
          <span>New Author</span>
        </motion.button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search authors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2 bg-gray-100 rounded-xl p-1 border border-gray-200">
            <motion.button
              onClick={() => setViewMode('grid')}
              whileTap={{ scale: 0.95 }}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode === 'grid' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiGrid} className="h-4 w-4" />
            </motion.button>
            <motion.button
              onClick={() => setViewMode('list')}
              whileTap={{ scale: 0.95 }}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode === 'list' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiList} className="h-4 w-4" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Authors Display */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">All Authors ({filteredAuthors.length})</h3>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading authors...</p>
          </div>
        ) : filteredAuthors.length > 0 ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6' : 'divide-y divide-gray-100'}>
            {filteredAuthors.map((author, index) => (
              <motion.div
                key={author.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={viewMode === 'grid' 
                  ? 'bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300 group'
                  : 'p-6 hover:bg-gray-50 transition-all duration-300'
                }
              >
                {viewMode === 'grid' ? (
                  <>
                    {/* Grid Card View */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          {author.avatar ? (
                            <img
                              src={author.avatar}
                              alt={author.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold">
                                {author.name?.charAt(0) || 'A'}
                              </span>
                            </div>
                          )}
                          <div>
                            {author.featured && (
                              <span className="bg-yellow-100 text-yellow-700 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1 border border-yellow-200 mb-1">
                                <SafeIcon icon={FiStar} className="h-3 w-3" />
                                <span>Featured</span>
                              </span>
                            )}
                            <span className={`text-xs font-medium px-2 py-1 rounded-full border ${
                              author.status === 'active' 
                                ? 'bg-green-100 text-green-700 border-green-200' 
                                : 'bg-gray-100 text-gray-700 border-gray-200'
                            }`}>
                              {author.status}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <motion.button
                            onClick={() => handleEdit(author)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-300"
                            title="Edit"
                          >
                            <SafeIcon icon={FiEdit2} className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDelete(author.id)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-300"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                          </motion.button>
                        </div>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">
                        {author.name}
                      </h3>
                      <p className="text-blue-600 text-sm font-medium mb-2 line-clamp-1">
                        {author.title}
                      </p>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {author.bio}
                      </p>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="text-center p-2 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-lg font-bold text-blue-600">
                            {authorStats[author.id]?.publishedPosts || 0}
                          </div>
                          <div className="text-xs text-gray-500">Articles</div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-lg font-bold text-green-600">
                            {author.yearsExperience || 0}
                          </div>
                          <div className="text-xs text-gray-500">Years Exp</div>
                        </div>
                      </div>

                      {/* Social Links */}
                      {author.social && (
                        <div className="flex items-center justify-center space-x-3">
                          {author.social.twitter && (
                            <a
                              href={author.social.twitter}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-600 transition-colors"
                            >
                              <SafeIcon icon={FiTwitter} className="h-4 w-4" />
                            </a>
                          )}
                          {author.social.linkedin && (
                            <a
                              href={author.social.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <SafeIcon icon={FiLinkedin} className="h-4 w-4" />
                            </a>
                          )}
                          {author.social.github && (
                            <a
                              href={author.social.github}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-gray-800 transition-colors"
                            >
                              <SafeIcon icon={FiGithub} className="h-4 w-4" />
                            </a>
                          )}
                          {author.website && (
                            <a
                              href={author.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-gray-800 transition-colors"
                            >
                              <SafeIcon icon={FiGlobe} className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  /* List View */
                  <div className="flex items-start justify-between">
                    <div className="flex space-x-4 flex-1">
                      {author.avatar ? (
                        <img
                          src={author.avatar}
                          alt={author.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-xl">
                            {author.name?.charAt(0) || 'A'}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{author.name}</h3>
                          {author.featured && (
                            <span className="bg-yellow-100 text-yellow-700 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1 border border-yellow-200">
                              <SafeIcon icon={FiStar} className="h-3 w-3" />
                              <span>Featured</span>
                            </span>
                          )}
                          <span className={`text-xs font-medium px-2 py-1 rounded-full border ${
                            author.status === 'active' 
                              ? 'bg-green-100 text-green-700 border-green-200' 
                              : 'bg-gray-100 text-gray-700 border-gray-200'
                          }`}>
                            {author.status}
                          </span>
                        </div>
                        <p className="text-blue-600 font-medium mb-2">{author.title}</p>
                        <p className="text-gray-600 mb-3 line-clamp-2">{author.bio}</p>
                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiFileText} className="h-4 w-4" />
                            <span>{authorStats[author.id]?.publishedPosts || 0} articles</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiCalendar} className="h-4 w-4" />
                            <span>{author.yearsExperience || 0} years exp</span>
                          </div>
                          {author.location && (
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FiMapPin} className="h-4 w-4" />
                              <span>{author.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <motion.button
                        onClick={() => handleEdit(author)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-300"
                        title="Edit"
                      >
                        <SafeIcon icon={FiEdit2} className="h-5 w-5" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleDelete(author.id)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-300"
                        title="Delete"
                      >
                        <SafeIcon icon={FiTrash2} className="h-5 w-5" />
                      </motion.button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <SafeIcon icon={FiUser} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-2">No authors yet</p>
            <p className="text-gray-500 mb-6">Create your first author profile to get started</p>
            <motion.button
              onClick={() => setIsEditing(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
            >
              Create First Author
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorManager;